import type { ReactNode } from 'react';
import { Pastille, PastilleSite } from '../ui/Primitives';
import {
  dateReunion,
  euros,
  heures,
  jourCourt,
  type FactureFormateur,
} from '../../lib/useFormateurs';

/**
 * Factures transmises par le formateur : période, montant déclaré, repère
 * heures validées × taux, coordonnées recopiées à la transmission, et ce que
 * la vérification en a dit. Les gestes sont fournis par l'écran.
 */
export function FacturesFormateur({
  factures,
  actions,
  coordonnees = false,
  siVide,
}: {
  factures: FactureFormateur[];
  actions: (facture: FactureFormateur) => ReactNode;
  /** Afficher SIRET et IBAN recopiés — ce que le vérificateur contrôle. */
  coordonnees?: boolean;
  siVide: string;
}): JSX.Element {
  if (factures.length === 0) {
    return <p className="px-4 py-5 text-sm text-codialis-gray sm:px-6">{siVide}</p>;
  }

  return (
    <ul className="divide-y divide-gray-100">
      {factures.map((facture) => {
        const ecart =
          facture.montantAttendu !== null ? Math.round((facture.montant - facture.montantAttendu) * 100) / 100 : null;
        return (
          <li key={facture.id} className="space-y-2 px-4 py-4 sm:px-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-codialis-navy">
                  Facture n° {facture.numero}
                  <PastilleSite site={facture.siteCode} />
                  {facture.typeLibelle && (
                    <Pastille texte={facture.typeLibelle} ton={facture.type === 'CFA' ? 'info' : 'mint'} />
                  )}
                </div>
                <div className="text-[11px] text-codialis-gray">
                  Du {jourCourt(facture.debut)} au {jourCourt(facture.fin)} · transmise le{' '}
                  {dateReunion(facture.transmiseLe)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-display text-base font-extrabold text-codialis-navy">
                  {euros(facture.montant)}
                </span>
                <Pastille texte={facture.etatLibelle} ton={facture.etatTon} />
              </div>
            </div>

            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
              <div className="min-w-0">
                <dt className="text-[10px] font-bold uppercase tracking-wide text-codialis-gray">Heures validées</dt>
                <dd className="text-codialis-navy">
                  {facture.heuresValidees > 0 ? heures(facture.heuresValidees) : 'Aucune fiche validée sur la période'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[10px] font-bold uppercase tracking-wide text-codialis-gray">Taux horaire</dt>
                <dd className="text-codialis-navy">
                  {facture.tauxHoraire !== null ? `${euros(facture.tauxHoraire)} HT` : 'Non fixé'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[10px] font-bold uppercase tracking-wide text-codialis-gray">Repère</dt>
                <dd className="text-codialis-navy">
                  {facture.montantAttendu !== null ? (
                    <>
                      {euros(facture.montantAttendu)} HT
                      {ecart !== null && ecart !== 0 && (
                        <span className="ml-1 text-amber-700">
                          (écart {ecart > 0 ? '+' : ''}
                          {euros(ecart)})
                        </span>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              {coordonnees && (
                <>
                  <div className="min-w-0">
                    <dt className="text-[10px] font-bold uppercase tracking-wide text-codialis-gray">SIRET</dt>
                    <dd className="font-mono text-codialis-navy">{facture.siret}</dd>
                  </div>
                  <div className="min-w-0 sm:col-span-2">
                    <dt className="text-[10px] font-bold uppercase tracking-wide text-codialis-gray">
                      IBAN {facture.bic ? '· BIC' : ''}
                    </dt>
                    <dd className="break-all font-mono text-codialis-navy">
                      {facture.iban ?? 'Illisible'}
                      {facture.bic ? ` · ${facture.bic}` : ''}
                    </dd>
                  </div>
                </>
              )}
            </dl>

            {facture.fiches.length > 0 && (
              <details className="text-xs text-codialis-gray">
                <summary className="cursor-pointer font-semibold text-codialis-navy">
                  {facture.fiches.length} fiche{facture.fiches.length > 1 ? 's' : ''} séquence rattachée
                  {facture.fiches.length > 1 ? 's' : ''}
                </summary>
                <ul className="mt-1 space-y-0.5">
                  {facture.fiches.map((fiche) => (
                    <li key={fiche.id}>
                      {jourCourt(fiche.date)} · {fiche.titre} · {heures(fiche.heures)}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {facture.motifRenvoi && facture.statut === 'A_CORRIGER' && (
              <p className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-800">
                Renvoyée le {dateReunion(facture.renvoyeeLe)}
                {facture.renvoyeePar ? ` par ${facture.renvoyeePar}` : ''} : {facture.motifRenvoi}
              </p>
            )}
            {facture.verifieeLe && facture.statut !== 'A_CORRIGER' && facture.statut !== 'TRANSMISE' && (
              <p className="text-[11px] text-codialis-gray">
                Vérifiée le {dateReunion(facture.verifieeLe)}
                {facture.verifieePar ? ` par ${facture.verifieePar}` : ''}
                {facture.statut === 'VERIFIEE' ? ' · prochaine étape : contrôle par le pôle facturation.' : ''}
                {facture.valideeLe
                  ? ` · validée le ${dateReunion(facture.valideeLe)}${facture.valideePar ? ` par ${facture.valideePar}` : ''}`
                  : ''}
                {facture.statut === 'VALIDEE' ? ' · part au prochain lot transmis à la Direction.' : ''}
                {facture.transmiseDirectionLe
                  ? ` · transmise à la Direction le ${dateReunion(facture.transmiseDirectionLe)}`
                  : ''}
              </p>
            )}
            {facture.statut === 'PAYEE' && facture.payeeLe && (
              <p className="rounded-xl bg-codialis-mint/10 px-3 py-2 text-xs text-codialis-navy">
                Payée le {jourCourt(facture.payeeLe)}
                {facture.referencePaiement ? ` · virement réf. ${facture.referencePaiement}` : ''}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">{actions(facture)}</div>
          </li>
        );
      })}
    </ul>
  );
}
